import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Imgs } from 'Assets/Img';
import { CreateOrganization } from 'Apollo/mutations/organization/createOrganization';
import { UpdateOrganization } from 'Apollo/mutations/organization/updateOrganization';
import { GetOrganizationDomains } from 'Apollo/queries/organization/GetOrganizationDomains';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'react-router';
import { PrivateRoutes } from 'Router/Routes';
import { useStyles } from './FormCreateOrganization.styles';
import TextInputDomains from 'Components/Shared/Form/Fields/TextInputDomains';

type BaseProps = {
  accountCode: string;
  accountLabel: string;
  requesterEmail?: string;
  onCancel(): void;
  onCompleted?(): void;
};

type CreateModeProps = BaseProps & {
  mode: 'Create';
};

type SelectModeProps = BaseProps & {
  mode: 'Select';
  organizationId: string;
  organizationName: string;
  initialDescription?: string;
};

type Props = CreateModeProps | SelectModeProps;

const FormCreateOrganization = (props: Props) => {
  const { accountCode, accountLabel, requesterEmail, onCancel, onCompleted, mode } = props;
  const ts = useTranslate();
  const classes = useStyles();
  const { onError, onSuccess } = useNotification();
  const history = useHistory();

  const isSelectMode = mode === 'Select';

  const [name, setName] = useState(isSelectMode ? props.organizationName : '');
  const [description, setDescription] = useState(isSelectMode ? (props.initialDescription ?? '') : '');
  const [domains, setDomains] = useState<string[]>([]);

  const { loading: domainsLoading } = useQuery<GetOrganizationDomainsData, { code: string }>(
    GetOrganizationDomains,
    {
      variables: { code: isSelectMode ? props.organizationId : '' },
      skip: !isSelectMode,
      onError,
      onCompleted: data => {
        setDomains(data.organizationDomains.edges.map(edge => edge.node.name));
      }
    }
  );

  const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  const allDomainsValid = domains.length > 0 && domains.every(d => domainRegex.test(d));

  const [createOrganization, { loading: createLoading }] = useMutation<
    CreateOrganizationData,
    CreateOrganizationVariables
  >(CreateOrganization, {
    onCompleted: (data) => {
      onSuccess('component.modals.createOrganization.success');
      onCompleted?.();
      onCancel();
      history.push(generatePath(PrivateRoutes.organizationProfileDomains.path, {
        accountCode,
        organizationCode: data.createOrganization
      }));
    },
    onError
  });

  const [updateOrganization, { loading: updateLoading }] = useMutation<
    UpdateOrganizationData,
    UpdateOrganizationVariables
  >(UpdateOrganization, {
    onCompleted: () => {
      onSuccess('component.modals.updateOrganization.success');
      onCompleted?.();
      onCancel();
      history.push(generatePath(PrivateRoutes.organizationProfileDomains.path, {
        accountCode,
        organizationCode: (props as SelectModeProps).organizationId
      }));
    },
    onError
  });

  const loading = createLoading || updateLoading || domainsLoading;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    if (isSelectMode) {
      updateOrganization({
        variables: {
          code: props.organizationId,
          accountId: accountCode,
          domains
        }
      });
    } else {
      createOrganization({
        variables: { name, domains, accountId: accountCode, description: description }
      });
    }
  };

  const isSubmitDisabled = (!isSelectMode && name.trim() === '') || !allDomainsValid || domainsLoading;

  const infoMessage = isSelectMode
    ? ts('component.modals.updateOrganization.info')
    : ts('component.modals.createOrganization.info');

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={7}>
          {requesterEmail && (
            <Typography variant="body2" className={classes.subtitle}>
              {ts('component.modals.createOrganization.requesterEmail', { email: requesterEmail })}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={ts('component.modals.createOrganization.labels.organizationName')}
                placeholder={ts('component.modals.createOrganization.placeholders.organizationName')}
                value={isSelectMode ? props.organizationName : name}
                onChange={e => setName(e.target.value)}
                disabled={isSelectMode}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={ts('component.modals.createOrganization.labels.description')}
                placeholder={ts('component.modals.createOrganization.placeholders.description')}
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={isSelectMode}
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={ts('component.modals.createOrganization.labels.customerAccount')}
                value={accountLabel}
                disabled
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextInputDomains
                label={ts('component.modals.createOrganization.labels.domainName')}
                value={domains}
                onChange={setDomains}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={5} className={classes.illustration}>
          <img
            src={Imgs.group.src}
            alt={Imgs.group.alt}
            className={classes.illustrationImg}
          />
        </Grid>
      </Grid>

      <Box mt={3} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box className={classes.infoBar} flex={1}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="caption">
            {infoMessage}
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Button onClick={onCancel} color="inherit">
            {ts('common.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitDisabled || loading}
          >
            {ts('component.modals.createOrganization.confirm')}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default FormCreateOrganization;
